import { ArrowRightOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Space } from 'antd'
import { FC, useCallback, useContext } from 'react'
import {
    useCreateCalculationPositionMutation,
    useDeleteCalculationPositionMutation,
} from '../../../../store/api/calculation-positions-api'
import {
    useDeleteCalculationMutation,
    useUpdateCalculationMutation,
} from '../../../../store/api/calculations-api'
import Calculation from '../../../../types/api/calculation'
import { PopconfirmProps } from 'antd/lib'
import useErrorModal from '../../../../hooks/api/use-error-modal'
import useSuccessModal from '../../../../hooks/api/use-success-modal'
import { CalculationPageCtx } from '../../calculations-page'
import { useAppSelector } from '../../../../store/store'
import { selectIsAnyPositionEditing } from '../../../../store/calculations/editing-positions-slice'

interface CalculationActionsProps {
    calculation: Calculation
}

const CalculationActions: FC<CalculationActionsProps> = ({ calculation }) => {
    const { onOpenDesignerTaskModal } = useContext(CalculationPageCtx)
    const isAnyPositionEditing = useAppSelector(state =>
        selectIsAnyPositionEditing(
            state,
            (calculation.calculation_positions ?? []).map(p => p.documentId)
        )
    )
    const [createPosition] = useCreateCalculationPositionMutation()
    const [updateCalculation] = useUpdateCalculationMutation()
    const [deleteCalculation, deleteCalculationState] = useDeleteCalculationMutation()
    const [deletePosition, deletePositionState] = useDeleteCalculationPositionMutation()

    const onAdd = useCallback(async () => {
        const result = await createPosition({ data: {} })
        const positionDocId = result.data?.data.documentId

        if ('data' in result && positionDocId) {
            updateCalculation({
                docId: calculation.documentId,
                body: {
                    data: {
                        calculation_positions: {
                            connect: [{ documentId: positionDocId, position: { start: true } }],
                        },
                    },
                },
            })
        }
    }, [createPosition, updateCalculation, calculation.documentId])

    const onSendToShipments = useCallback(() => {
        onOpenDesignerTaskModal(calculation)
    }, [onOpenDesignerTaskModal, calculation])

    const onRemoveCalculation = async () => {
        await deleteCalculation(calculation.documentId)
    }

    const onRemovePositions = async () => {
        await calculation.calculation_positions?.forEach(position => {
            deletePosition(position.documentId)
        })
    }

    const confirm: PopconfirmProps['onConfirm'] = e => {
        onRemoveCalculation()
        onRemovePositions()
    }
    useSuccessModal(deleteCalculationState, 'Расчет удален')
    useSuccessModal(deletePositionState, 'Позиции удалены')

    useErrorModal(deletePositionState, 'Ошибка удаления позиции')
    useErrorModal(deleteCalculationState, 'Ошибка удаления расчета')

    return (
        <Space>
            <Button
                size='small'
                title='Добавить позицию'
                icon={<PlusOutlined />}
                onClick={onAdd}
                color={'green'}
                variant={'outlined'}
            />
            <Button
                size='small'
                type={'primary'}
                title={
                    isAnyPositionEditing
                        ? 'Дождитесь сохранения изменений'
                        : 'Отправить задание дизайнеру и перенести в отгрузки'
                }
                icon={<ArrowRightOutlined />}
                onClick={onSendToShipments}
                variant={'outlined'}
                disabled={isAnyPositionEditing}
            />
            <Popconfirm
                title='Удалить расчет'
                description='Вы уверены?'
                onConfirm={confirm}
                okText='Да'
                cancelText='Нет'
            >
                <Button
                    size='small'
                    title='Удалить расчет'
                    icon={<DeleteOutlined />}
                    danger
                    color='danger'
                    variant={'solid'}
                />
            </Popconfirm>
        </Space>
    )
}

export default CalculationActions
